import type {
  FulfillmentLineItem,
  FulfillmentLineItemConnection,
  Image,
  Metafield,
  OrderLineItem,
  Product,
  ProductVariant,
  ProductVariantConnection,
  MoneyV2,
  ProductVariantEdge,
} from "@shopify/hydrogen-react/storefront-api-types";
import { query } from "gql-query-builder";
import type IQueryBuilderOptions from "gql-query-builder/build/IQueryBuilderOptions";

import { APP_NAME, imageFields, optionFields, productFields, productVariantFields } from "./fields";
import { instagramMetafield, isOfflineMetafield } from "./metafields/order-metafields";
import {
  additionalDescriptionMetafield,
  availableCountriesMetafield,
  categoryMetafield,
  dropDateMetafield,
  femaleModelMetafield,
  hiddenMetafield,
  launchMetafield,
  maleModelMetafield,
  modelMetafield,
  noticeMetafield,
  preorderMetafield,
  productVariantMetafield,
  quantityLimitMetafield,
  quantityLimitProductMetafield,
  recommendedStyleMetafield,
  relatedCollectionMetafield,
  releaseDateMetafield,
  seungsuStockMetafield,
  sizeChartMetafield,
  sizeFitMetafield,
  tagMetafield,
  membershipMetafield,
  warningMetafield,
  informationMetafield,
  employeeOnlyMetafield,
  brandVariantIdMetafield,
  saleMetafield,
} from "./metafields/product-metafields";
import {
  productVariantFragment,
  seongsuVariantStockMetafield,
  variantMetafields,
  yeonhuiVariantStockMetafield,
} from "./fragments";

// customer order에 있는 product의 메타필드 정의 (쿼리 너무 길면 에러남)
// error message ex) "Query has a depth of 18, which exceeds the max depth of 17."
export const customerProductMetafields: Record<string, IQueryBuilderOptions[]> = {
  tune: [
    sizeChartMetafield,
    categoryMetafield,
    noticeMetafield,
    dropDateMetafield,
    isOfflineMetafield,
    instagramMetafield,
    additionalDescriptionMetafield,
    quantityLimitMetafield,
    launchMetafield,
    seungsuStockMetafield,
    hiddenMetafield,
    preorderMetafield,
  ],
  yeseyesee: [
    sizeChartMetafield,
    tagMetafield,
    additionalDescriptionMetafield,
    noticeMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
    hiddenMetafield,
  ],
  thisisneverthat: [
    noticeMetafield,
    sizeFitMetafield,
    hiddenMetafield,
    relatedCollectionMetafield,
    maleModelMetafield,
    femaleModelMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
    availableCountriesMetafield,
    releaseDateMetafield,
  ],
  khakis: [hiddenMetafield, quantityLimitMetafield, quantityLimitProductMetafield],
  jknd: [
    noticeMetafield,
    maleModelMetafield,
    femaleModelMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
    saleMetafield,
    sizeChartMetafield,
  ],
};

export const productMetafields: Record<string, IQueryBuilderOptions[]> = {
  tune: [
    sizeChartMetafield,
    categoryMetafield,
    noticeMetafield,
    dropDateMetafield,
    isOfflineMetafield,
    instagramMetafield,
    additionalDescriptionMetafield,
    quantityLimitMetafield,
    launchMetafield,
    seungsuStockMetafield,
    hiddenMetafield,
    preorderMetafield,
    membershipMetafield,
  ],
  yeseyesee: [
    sizeChartMetafield,
    tagMetafield,
    additionalDescriptionMetafield,
    noticeMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
    hiddenMetafield,
  ],
  thisisneverthat: [
    noticeMetafield,
    sizeFitMetafield,
    hiddenMetafield,
    relatedCollectionMetafield,
    maleModelMetafield,
    femaleModelMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
    availableCountriesMetafield,
    releaseDateMetafield,
    employeeOnlyMetafield,
  ],
  khakis: [
    sizeChartMetafield,
    recommendedStyleMetafield,
    modelMetafield,
    relatedCollectionMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
    hiddenMetafield,
    tagMetafield,
    warningMetafield,
    noticeMetafield,
    informationMetafield,
  ],
  jknd: [
    sizeChartMetafield,
    noticeMetafield,
    maleModelMetafield,
    femaleModelMetafield,
    quantityLimitMetafield,
    quantityLimitProductMetafield,
  ],
};

export const variants = (first: number): object => {
  return {
    operation: `variants(first: ${first})`,
    fields: [
      {
        edges: [
          {
            node: [
              ...productVariantFields,
              productVariantMetafield,
              brandVariantIdMetafield,
              ...variantMetafields[APP_NAME],
            ],
          },
        ],
      },
    ],
    variables: {},
  };
};

export const images = {
  operation: "images(first: 50)",
  fields: [{ edges: [{ node: imageFields }] }],
  variables: {},
};

export interface IProductMetafields {
  launch?: Metafield;
  category?: Metafield;
  sizeChart?: Metafield;
  notice?: Metafield;
  isOffline?: Metafield;
  instagram?: Metafield;
  additionalDescription?: Metafield;
  quantityLimit?: Metafield;
  quantityLimitProduct?: Metafield;
  tag?: Metafield;
  hidden?: Metafield;
  employeeOnly?: Metafield;
  seoungsu_stock?: Metafield;
  preorder?: Metafield;
  size_fit?: Metafield;
  related_collection?: Metafield;
  maleModel?: Metafield;
  femaleModel?: Metafield;
  available_countries?: Metafield;
  release_date?: Metafield;
  recommended_style?: Metafield;
  model?: Metafield;
  accessible_memberships?: Metafield;
  warning?: Metafield;
  information?: Metafield;
  sale?: Metafield;
}

export interface IProductVariantMetafields {
  brandVariantId?: Metafield;
}

export const commonProductFields = [
  ...productFields,
  ...productMetafields[APP_NAME],
  {
    operation: `collections(first: 20)`,
    fields: [{ edges: [{ node: ["title", "handle", "description"] }] }],
    variables: {},
  },
  variants(40),
];

export const commonCustomerProductFields = [...productFields, ...customerProductMetafields[APP_NAME], variants(40)];

export interface IProductDetailNode extends ProductVariant {
  restock?: Metafield;
  seongsuStock?: Metafield;
  yeonhuiStock?: Metafield;
}

/**
 * 상품 상세 조회
 */
export interface IProductDetailVariants extends ProductVariantConnection {
  edges: (ProductVariantEdge & { node: IProductDetailNode })[];
}

export interface IProductDetailQuery {
  product?: Product &
    IProductMetafields & {
      variants: IProductDetailVariants;
      hidden?: Metafield;
    };
}

export const productDetailQuery = query(
  {
    operation: "product",
    fields: [...commonProductFields, images, { options: optionFields }],
    variables: { handle: { type: "String!" } },
  },
  undefined,
  { operationName: "ProductDetail" },
);

/**
 * 상품 상세 추천 상품 조회
 */
export interface IProductDetailRecommendQuery {
  productRecommendations: IProductDetailQuery["product"][];
}

export const productDetailRecommendQuery = query(
  {
    operation: "productRecommendations",
    fields: [...commonProductFields, images],
    variables: {
      productId: { type: "ID!" },
      intent: { type: "ProductRecommendationIntent" },
    },
  },
  undefined,
  { operationName: "ProductRecommendations" },
);

/**
 * 상품 variant 조회
 */
export interface IProductVariantQuery {
  node: ProductVariant & {
    product: Product & IProductMetafields;
  };
}

export const productVariantQuery = query(
  {
    operation: "node",
    fields: [
      {
        ...productVariantFragment,
        fields: [
          ...productVariantFields,
          dropDateMetafield,
          launchMetafield,
          seongsuVariantStockMetafield,
          yeonhuiVariantStockMetafield,
        ],
      },
    ],
    variables: { id: { type: "ID!" } },
  },
  undefined,
  { operationName: "ProductVariant" },
);

export interface IReturnOrder extends FulfillmentLineItem {
  id: string;
  quantity: number;
  index: number;
  lineItem: OrderLineItem & {
    returnReason?: string;
    id: string;
    originalTotalSet: { shopMoney: MoneyV2 };
    totalDiscountSet: { shopMoney: MoneyV2 };
    image: Image;
    vendor: string;
    variant: ProductVariant & {
      price: string | MoneyV2;
    };
  };
}

export interface IReturnLineItemNode {
  id: string;
  returnReason: string;
  quantity: number;
  fulfillmentLineItem: FulfillmentLineItem & {
    id: string;
    lineItem: OrderLineItem & {
      originalTotalSet: { shopMoney: MoneyV2 };
      totalDiscountSet: { shopMoney: MoneyV2 };
      discountedUnitPriceSet: { shopMoney: MoneyV2 };
      originalUnitPriceSet: { shopMoney: MoneyV2 };
      id: string;
      image: Image;
      vendor: string;
      variant: ProductVariant;
    };
  };
}

export interface IReturnOrderFulfill extends FulfillmentLineItemConnection {
  nodes: IReturnOrder[];
}

export interface IReturnOrderLambda {
  id: string;
  returns: {
    nodes: {
      status: string;
      name: string;
      order: {
        fulfillments: {
          id: string;
          fulfillmentLineItems: IReturnOrderFulfill;
        }[];
      };
      returnLineItems: {
        nodes: IReturnLineItemNode[];
      };
    }[];
  };
  lineItems: {
    edges: {
      node: {
        id: string;
        refundableQuantity: number;
      };
    }[];
  };
}
